import type { LocaleDefinition } from "../locale-definition";

import type { EnglishTranslation } from "./en";

const translation = {
  actions: {
    add: "Ajouter",
    apply: "Appliquer",
    back: "Retour",
    cancel: "Annuler",
    clear: "Effacer",
    close: "Fermer",
    confirm: "Confirmer",
    continue: "Continuer",
    create: "Créer",
    delete: "Supprimer",
    edit: "Modifier",
    remove: "Retirer",
    reset: "Réinitialiser",
    retry: "Réessayer",
    save: "Enregistrer",
    search: "Rechercher",
    submit: "Soumettre",
    signIn: "Se connecter",
    rememberMe: "Se souvenir de moi",
    forgotPassword: "Mot de passe oublié?",
    returnToDashboard: "Retourner au tableau de bord",
    backToLogin: "Retour à la connexion",
    sendResetInstructions: "Envoyer les instructions",
    resetPassword: "Réinitialiser le mot de passe",
    addUser: "Ajouter un utilisateur",
    previous: "Précédent",
    next: "Suivant",
    createUser: "Créer un utilisateur",
    regeneratePassword: "Régénérer",
    backToUsers: "Retour aux utilisateurs",
    lock: "Verrouiller le compte",
    unlock: "Déverrouiller le compte",
    locking: "Verrouillage...",
    unlocking: "Déverrouillage...",
    upload: "Téléverser",
    sendReminder: "Envoyer un rappel",
    updatePayee: "Modifier le bénéficiaire",
    submitUpdate: "Soumettre la modification",
    backToStores: "Retour aux magasins",
  },

  state: {
    yes: "Oui",
    no: "Non",
    none: "Aucun",
    enabled: "Activé",
    disabled: "Désactivé",
    active: "Actif",
    inactive: "Inactif",
    locked: "Verrouillé",
    unlocked: "Déverrouillé",
    adding: "Ajout...",
    loadingRoles: "Chargement des rôles...",
    creating: "Création...",
    saving: "Enregistrement…",
    loadingStoreDetails: "Chargement du magasin…",
    loadingPayeeDetails: "Chargement des détails du bénéficiaire…",
  },

  fields: {
    emailAddress: "Adresse courriel",
    username: "Nom d'utilisateur",
    password: "Mot de passe",
    newPassword: "Nouveau mot de passe",
    confirmPassword: "Confirmer le mot de passe",
    role: "Rôle",
    status: "Statut",
    firstName: "Prénom",
    lastName: "Nom",
    createdAt: "Créé le",
    fullName: "Nom complet",
    accountStatus: "État du compte",
    createdOn: "Créé le",
    lastModified: "Dernière modification",
    code: "Code",
    address: "Adresse",
    email: "Courriel",
    phone: "Téléphone",
    searchStores: "Rechercher des magasins",
  },

  placeholders: {
    email: "utilisateur@exemple.com",
    username: "Saisissez votre nom d'utilisateur",
    password: "Saisissez votre mot de passe",
    newPassword: "Entrez votre nouveau mot de passe",
    confirmPassword: "Confirmez votre nouveau mot de passe",
    searchStores: "Nom, code ou ville du magasin",
  },

  titles: {
    applicationError: "Erreur d'application",
    pageNotFound: "Page non trouvée",
    unauthorized: "Non autorisé",
    protectedWorkspace: "Espace de travail protégé",
  },

  messages: {
    pageNotFoundDescription: "La page demandée n'existe pas.",
    unauthorizedDescription:
      "Vous n'avez pas la permission d'accéder à cette page.",
    signingIn: "Connexion en cours...",
    signedOut: "Vous avez été déconnecté.",
    signOutFailed: "La déconnexion a échoué. Veuillez réessayer.",
    sendingResetInstructions: "Envoi des instructions...",
    resettingPassword: "Réinitialisation du mot de passe...",
    userCreated: "Utilisateur créé avec succès.",
    loadFailed: "Impossible de charger l’utilisateur.",
    lockSuccess: "Le compte utilisateur a été verrouillé avec succès.",
    unlockSuccess: "Le compte utilisateur a été déverrouillé avec succès.",
    failed: "L’action a échoué. Veuillez réessayer.",
    storeNotFound: "Magasin introuvable",
    storeNotFoundHint:
      "Le magasin sélectionné n’existe pas ou n’est plus disponible.",
  },

  errors: {
    invalidCredentials:
      "Le nom d'utilisateur ou le mot de passe est incorrect.",
    unauthorized: "Vous n'avez pas la permission d'accéder à cette page.",
    roleNotFound: "Le rôle sélectionné n'existe pas.",
    roleInactive: "Le rôle sélectionné est inactif.",
    storeNotFound: "Le magasin demandé est introuvable.",
    emptyData: "Aucune donnée disponible à l'affichage.",
    invalidInvitationToken: "Le jeton d’invitation est invalide ou a expiré.",
    customerInvitationExpired: "L'invitation pour ce client a expiré.",
    statusNotConfigured: "Le statut n'est pas configuré.",
    payeeCreationFailed: "Impossible d’ajouter le bénéficiaire. Réessayez.",
    customerNotFound: "Client introuvable.",
    payeeNotFound: "Bénéficiaire introuvable.",
    userNotFound: "Utilisateur introuvable.",
    EmptyData: "Aucune donnée à afficher.",
    customerAccountAlreadyExists: "Un compte client existe déjà.",
    userNameAlreadyExists: "Ce nom d’utilisateur est déjà utilisé.",
    dataInInvalidState: "Les données sont invalides.",
    invalidProvince: "Province invalide.",
    invalidPaymentMethod: "Mode de paiement invalide.",
    addressTypeNotConfigured: "Type d’adresse non configuré.",
    payeePendingStatusNotConfigured: "Statut en attente non configuré.",
    userLockFailed: "Impossible de verrouiller le compte. Réessayez.",
    UserUnlockFailed: "Impossible de déverrouiller le compte. Réessayez.",
    errorLoadingPayeeDetails:
      "Impossible de charger les coordonnées du bénéficiaire. Veuillez réessayer.",
  },

  language: {
    label: "Langue",
    switchTo: "Passer à {{language}}",
  },

  loginPage: {
    eyebrow: "MARS",

    title: "Connectez-vous à votre espace de travail",

    description:
      "Utilisez les identifiants de votre organisation pour accéder à la Plateforme MARS.",

    welcomeBack: "Accès entreprise sécurisé",

    brandBadge: 'Espace magasins et représentants',

    brandTitle: 'Reliez magasins, représentants et paiements.',

    brandDescription:
      'Gérez les relations avec les magasins, les affectations territoriales et les renseignements sur les bénéficiaires au même endroit. Suivez les inscriptions et consultez les détails de paiement des magasins qui vous sont attribués.',

    benefits: {
      clientReady: {
        title: 'Magasins et représentants',
        description:
          'Consultez les magasins ainsi que leurs représentants, territoires et districts.',
      },

      workflows: {
        title: 'Inscription des bénéficiaires',
        description:
          'Invitez les magasins, recueillez les renseignements sur les bénéficiaires et suivez les inscriptions.',
      },

      visibility: {
        title: 'Gestion des paiements',
        description:
          'Consultez les préférences de paiement par TEF ou par chèque, les coordonnées bancaires et le statut d’approbation des bénéficiaires.',
      },
    },

    securePlatform:
      'Accédez aux renseignements sur les magasins et les paiements selon le rôle qui vous est attribué.',

    secureAccess:
      "Accès protégé par vos identifiants professionnels et vos autorisations.",

    copyright: "© {{year}} Plateforme Paygos - Mars. Tous droits réservés.",
  },

  forgotPasswordPage: {
    badge: "Récupération du mot de passe",
    title: "Mot de passe oublié ?",
    description:
      "Entrez votre nom d’utilisateur pour recevoir les instructions de réinitialisation.",
    successTitle: "Vérifiez votre courriel",
    successMessage:
      "Si ce nom d’utilisateur existe, les instructions ont été envoyées.",
    securityMessage:
      "Par sécurité, nous ne confirmons pas l’existence d’un compte.",
  },
  resetPasswordPage: {
    badge: "Réinitialisation du mot de passe",
    title: "Créer un nouveau mot de passe",
    description: "Entrez et confirmez votre nouveau mot de passe.",
    successTitle: "Mot de passe mis à jour",
    successMessage: "Votre mot de passe a été réinitialisé.",
  },

  customerSignup: {
    page: {
      brandingBadge: "Inscription sécurisée des clients",
      brandingTitle: "Bienvenue sur Mars",
      brandingDescription:
        "Terminez la création de votre compte pour accéder en toute sécurité aux renseignements, aux paiements et aux services de votre magasin.",
      brandingFooter: "Inscription sécurisée des clients Mars",
      benefits: {
        secureAccessTitle: "Accès sécurisé au compte",
        secureAccessDescription:
          "Créez vos identifiants Mars personnels à partir de votre invitation vérifiée.",
        storeInformationTitle: "Renseignements sur votre magasin",
        storeInformationDescription:
          "Consultez le profil et les renseignements associés à votre compte.",
        simpleOnboardingTitle: "Inscription simplifiée",
        simpleOnboardingDescription:
          "Configurez votre compte une seule fois pour accéder directement à la plateforme Mars.",
        connectedPlatformTitle: "Une plateforme intégrée",
        connectedPlatformDescription:
          "Gérez vos activités, vos paiements et les services à venir au même endroit.",
      },
      loadingTitle: "Chargement de votre invitation",
      loadingDescription:
        "Veuillez patienter pendant la vérification de vos renseignements.",
      invitationErrorTitle: "Impossible de poursuivre",
      invitationErrorDescription:
        "Impossible de charger votre invitation. Elle est peut-être invalide ou expirée.",
      successBadge: "Configuration du compte terminée",
      successTitle: "Compte créé",
      successDescription:
        "Votre compte Mars a été créé. Vous pouvez maintenant vous connecter.",
      usernameLabel: "Votre nom d’utilisateur",
      setupBadge: "Configuration du compte client",
      setupTitle: "Terminez la configuration de votre compte",
      setupDescription:
        "Vérifiez vos renseignements et créez vos identifiants de connexion Mars sécurisés.",
      customerInformationTitle: "Renseignements sur le client",
      customerInformationDescription:
        "Vérifiez les renseignements du magasin associés à cette invitation.",
      createLoginTitle: "Créez vos identifiants",
      createLoginDescription:
        "Choisissez votre nom, votre nom d’utilisateur et votre mot de passe.",
      createAccountErrorTitle: "Impossible de créer le compte",
      unexpectedError:
        "Une erreur inattendue s’est produite. Veuillez réessayer.",
      secureAccountTitle: "Création de compte sécurisée",
      secureAccountDescription:
        "Cette page est accessible uniquement à partir de votre invitation client. Votre mot de passe est géré de façon sécurisée par l’authentification Mars.",
      copyright: "© {{year}} Mars. Tous droits réservés.",
    },
    customerInformation: {
      customerName: "Nom du client",
      customerCode: "Code du client",
      contactNote:
        "Ces renseignements ont été fournis lors de la configuration de votre compte client. Si une information est incorrecte, veuillez communiquer avec votre représentant commercial.",
    },
    accountSetup: {
      passwordRequirementsTitle: "Exigences du mot de passe",
      passwordRequirementsDescription: "Utilisez au moins 12 caractères.",
      createAccount: "Créer un compte",
      creatingAccount: "Création du compte...",
    },
  },

  userSearchPage: {
    title: "Utilisateurs",
    description: "Recherchez et gérez les utilisateurs de l’application.",
    searchLabel: "Rechercher des utilisateurs",
    searchPlaceholder: "Rechercher par nom d’utilisateur, courriel ou nom",
    noUsers: "Aucun utilisateur trouvé.",
    totalUsers: "{{count}} utilisateurs",
  },

  addUserPage: {
    title: "Ajouter un utilisateur",
    description:
      "Créez un nouvel utilisateur de l'application et attribuez-lui un rôle.",
    selectRole: "Sélectionner un rôle",
    passwordHint:
      "Un mot de passe temporaire a été généré. Vous pouvez le modifier ou le régénérer avant de créer le compte.",
  },

  userDetail: {
    title: "Détails de l’utilisateur",
    description:
      "Consultez les informations de l’utilisateur et gérez l’accès au compte.",
    invalidUser: "L’utilisateur demandé est invalide.",

    accountInformation: {
      title: "Informations du compte",
      description: "Informations sur l’identité, le rôle et l’état du compte.",
    },

    access: {
      title: "Accès au compte",
      unlockedDescription:
        "Ce compte est déverrouillé. Le verrouiller empêchera toute nouvelle connexion.",
      lockedDescription:
        "Ce compte est verrouillé. Le déverrouiller permettra de se connecter à nouveau.",
      sessionNote:
        "Le verrouillage ne ferme pas les sessions déjà ouvertes. Leur gestion sera traitée séparément.",
    },
  },

  storeDetail: {
    tabsLabel: "Détails du magasin",
    storeCode: "Code du magasin",
    storeInformation: "Renseignements sur le magasin",
    customerType: "Type de client",
    parentStore: "Magasin parent",
    primaryAddress: "Adresse principale",
    city: "Ville",
    province: "Province",
    postalCode: "Code postal",
    addressType: "Type d’adresse",
    noAddress: "Aucune adresse disponible.",
    territoryCode: "Code de territoire",
    district: "District",
    noTerritory: "Aucun territoire attribué.",
    paymentInformation: "Renseignements de paiement",
    payeeStatus: "Statut du bénéficiaire",
    noPaymentInformation: "Aucun renseignement de paiement disponible.",
    paymentHistoryPending: "L’historique des paiements s’affichera ici.",
    sendInvitation: "Envoyer une invitation",
    sendInvitationDescription:
      "Saisissez l’adresse courriel à laquelle l’invitation du client doit être envoyée.",
    sendingInvitation: "Envoi en cours...",
    invitationEmailPlaceholder: "client@exemple.ca",
    invitationFailed: "Impossible d’envoyer l’invitation. Veuillez réessayer.",
    digits: "{{field}} doit contenir exactement {{count}} chiffres.",
    overview: "Aperçu",
    payee: "Bénéficiaire",
    payments: "Paiements",
    activity: "Activité",
    summary: "Résumé du magasin",
    payeeDetails: "Coordonnées du bénéficiaire",
    banking: "Coordonnées bancaires",
    recentPayments: "Paiements récents",
    timeline: "Historique d’inscription",
    contact: "Contact du bénéficiaire",
    validation: "Validation AFS",
    history: "Historique des soumissions",
    viewAll: "Tout afficher",
    approved: "Approuvé",
    submitted: "Soumis",
    passed: "Réussie",
    pending: "En attente",
    payeeName: "Nom du bénéficiaire",
    legalName: "Dénomination légale",
    bank: "Banque",
    institution: "Numéro d’institution",
    transit: "Numéro de transit",
    account: "Numéro de compte",
    lastFour: "Quatre derniers chiffres du compte",
    currency: "Devise",
    effective: "En vigueur depuis",
    masterAccount: "Compte principal",
    segment: "Segment",
    territory: "Territoire",
    dsm: "DSM",
    paymentNumber: "Paiement nº",
    type: "Type",
    issued: "Émis le",
    amount: "Montant",
    status: "Statut",
    cleared: "Réglé",
    cashed: "Encaissé",
    date: "Date",
    event: "Événement",
    by: "Par",
    paidYear: "Payé en {{year}}",
    average: "Moyenne",
    nextRun: "Prochain paiement",
    settles: "Les fonds sont réglés sous deux jours ouvrables.",
    noScheduled: "Aucun paiement planifié.",
    noPayee: "Aucune coordonnée de bénéficiaire",
    noPayeeHint:
      "Les coordonnées apparaîtront après leur soumission par le magasin.",
    noPayments: "Aucun paiement trouvé",
    noPaymentsHint: "Aucun paiement ne correspond à cette année et à ce mode.",
    noHistory: "Aucune soumission",
    activityHint: "Tous les événements liés à ce magasin.",
    eventTypes: "Types d’événements",
    enrolment: "Inscription",
    payeeChanges: "Modifications du bénéficiaire",
    invitations: "Invitations",
    year: "Année de paiement",
    verified: "Coordonnées bancaires vérifiées par AFS le {{date}}.",
    notVerified: "Validation en attente.",
    detailsPending:
      "Les détails du bénéficiaire, des paiements et des activités ne sont pas encore disponibles.",
    demo: "Données de démonstration · modifications conservées jusqu’au rechargement.",
    reminderDescription:
      "Enregistrez un rappel de démonstration pour ce contact. Le lien d’invitation reste valide. Aucun courriel ne sera envoyé.",
    lastReminder: "Dernier rappel",
    never: "Aucun",
    daysAgo: "Le dernier rappel date de {{count}} jours.",
    reminderSaved: "Rappel de démonstration enregistré. Aucun courriel envoyé.",
    payeeSaved: "Modification de démonstration soumise pour examen.",

    demoForm:
      "Utilisez uniquement des données fictives. Aucun compte bancaire réel ne sera modifié.",
    noInvitation: "Ce magasin n’a pas encore été invité.",
    paymentReleased: "Paiement TEF émis",
    payeeApproved: "Bénéficiaire approuvé",
    bankingSubmitted: "Coordonnées bancaires soumises",
    signedIn: "Connexion du magasin",
    invitationSent: "Invitation envoyée",
    chequeIssued: "Chèque émis",
    storeAdded: "Magasin ajouté au territoire",
    reminderRecorded: "Rappel de démonstration enregistré",
    payeeUpdated: "Modification du bénéficiaire soumise",
    storeActor: "Magasin",
    systemActor: "Système",
    demoActor: "Utilisateur de démonstration",
    bankingDescription:
      "Renseignements de paiement actuels pour ce bénéficiaire",
    name: "Nom",
  },

  storeSearch: {
    filtersError: "Impossible de charger les options de filtre",

    summaryError: "Impossible de charger les totaux des magasins",
    filtersUnavailable:
      "Les filtres supplémentaires et le tri ne sont pas encore disponibles.",
    enrolled: "Inscription terminée",
    customerType: "Type de client",
    independent: "Indépendant",
    group: "Groupe",
    selectedFilters: "Filtres sélectionnés",
    removeFilter: "Retirer {{filter}}",
    title: "Recherche de magasins",
    description:
      "Recherchez les magasins de votre territoire et suivez leur inscription.",
    breadcrumb: "Magasins et bénéficiaires",
    sample: "Données de démonstration",
    invite: "Inviter un magasin",
    inviteUnavailable:
      "Les invitations aux magasins ne sont pas encore disponibles.",
    territory: "Magasins du territoire",
    territoryHint: "Territoire F026",
    eft: "Paiement par TEF",
    eftHint: "{{percent}} % de votre territoire",
    invited: "Invitations en attente",
    invitedHint: "En attente de connexion",
    notInvited: "Pas encore invités",
    notInvitedHint: "Aucune invitation envoyée",
    all: "Tous",
    invitedTab: "Invitation envoyée",
    notInvitedTab: "Non invités",
    sort: "Trier par",
    nameAsc: "Nom du magasin : A–Z",
    nameDesc: "Nom du magasin : Z–A",
    paymentNewest: "Dernier paiement : récent",
    paymentOldest: "Dernier paiement : ancien",
    invitedNewest: "Invitation : récente",
    invitedOldest: "Invitation : ancienne",
    filter: "Filtrer",
    paymentFilter: "Mode de paiement",
    allPayments: "Tous les modes de paiement",
    cheque: "Chèque",
    noPayment: "Non défini",
    store: "Magasin",
    status: "Statut",
    payee: "Bénéficiaire",
    payment: "Mode de paiement",
    lastPayment: "Dernier paiement",
    invitedOn: "Invité le",
    empty: "Aucun magasin trouvé",
    emptyHint: "Essayez une autre recherche ou effacez les filtres.",
    clear: "Effacer les filtres",
    details: "Afficher les détails de {{name}}",
    storeCode: "Code du magasin",
    location: "Emplacement",
    close: "Fermer",
    results: "{{count}} magasins trouvés",
  },

  payee: {
    addNew: "Ajouter un bénéficiaire",
    addDescription: "Ajouter les renseignements du bénéficiaire et du paiement",
    personalInformation: "Renseignements personnels",
    fullName: "Nom complet",
    email: "Courriel",
    phone: "Téléphone",
    address: "Adresse",
    city: "Ville",
    province: "Province",
    postalCode: "Code postal",
    paymentMethod: "Mode de paiement",
    eft: "TEF",
    cheque: "Chèque",
    bankingInformation: "Renseignements bancaires",
    bankInstitution: "Institution bancaire",
    transitNumber: "Numéro de transit",
    accountNumber: "Numéro de compte",
    bankingDocument: "Document bancaire",

    createError: "Impossible d’ajouter le bénéficiaire. Veuillez réessayer.",
  },

  navigation: {
    applicationName: "MARS",
    applicationDescription: "Plateforme de paiements",

    menu: "Menu",
    dashboard: "Tableau de bord",
    uiComponents: "Composants UI",
    users: "Utilisateurs",
    eftPendingApproval: "TEF en attente d’approbation",
    mainNavigation: "Navigation principale",
    addUser: "Ajouter un utilisateur",

    stores: {
      label: "Magasins",
      storePayee: "Recherche de magasin",
      description: "Gérez les magasins et leurs bénéficiaires.",
      emptyTitle: "Aucun magasin disponible",
      emptyDescription:
        "Les magasins et les bénéficiaires apparaîtront ici après la connexion à l’API.",
    },

    orders: {
      label: "Commandes",
      all: "Toutes les commandes",
      new: "Nouvelle commande",
      history: "Historique des commandes",
    },

    user: {
      guest: "Utilisateur invité",
      signIn: "Se connecter",
      signInToContinue: "Connectez-vous pour continuer",
    },

    sidebar: {
      expand: "Développer la barre latérale",
      collapse: "Réduire la barre latérale",
      open: "Ouvrir la navigation",
      close: "Fermer la navigation",
    },

    theme: {
      light: "Passer au thème clair",
      dark: "Passer au thème sombre",
    },

    client: {
      development: "Client de développement",
    },
  },

  validation: {
    required: "{{field}} est obligatoire.",
    invalidEmail: "Saisissez une adresse courriel valide.",
    minimumLength: "{{field}} doit contenir au moins {{minimum}} caractères.",
    maximumLength:
      "{{field}} ne peut pas contenir plus de {{maximum}} caractères.",
    invalidDate: "Saisissez une date valide.",
    invalidFileType: "Ce type de fichier n’est pas autorisé.",
    fileTooLarge: "Le fichier dépasse la taille maximale autorisée.",
    fileTooLargeWithLimit: "Le fichier dépasse la limite de {{size}}.",
    maximumFiles_one: "Maximum : {{count}} fichier.",
    maximumFiles_other: "Maximum : {{count}} fichiers.",
    passwordsDoNotMatch: "Les mots de passe ne correspondent pas.",
    userNameRequired: "Le nom d'utilisateur est requis.",
    emailRequired: "L'adresse courriel est requise.",
    emailInvalid: "Entrez une adresse courriel valide.",
    firstNameRequired: "Le prénom est requis.",
    lastNameRequired: "Le nom est requis.",
    passwordRequired: "Le mot de passe est requis.",
    confirmPasswordRequired: "Confirmez votre mot de passe.",
    passwordMismatch: "Les mots de passe ne correspondent pas.",
    roleRequired: "Sélectionnez un rôle.",
    passwordMinLength: "Le mot de passe doit contenir au moins 12 caractères.",
    passwordUppercase:
      "Le mot de passe doit contenir au moins une lettre majuscule.",
    passwordLowercase:
      "Le mot de passe doit contenir au moins une lettre minuscule.",
    passwordNumber: "Le mot de passe doit contenir au moins un chiffre.",
    passwordSpecial:
      "Le mot de passe doit contenir au moins un caractère spécial.",
  },

  ui: {
    alertDialog: {
      instruction: "Choisissez une action ci-dessous pour continuer.",
    },

    breadcrumbs: {
      label: "Fil d’Ariane",
    },

    dialog: {
      close: "Fermer la boîte de dialogue",
    },

    password: {
      show: "Afficher le mot de passe",
      hide: "Masquer le mot de passe",
    },

    select: {
      placeholder: "Sélectionner une option",
      openOptions: "Ouvrir les options",
      noOptions: "Aucune option disponible",
    },

    multiSelect: {
      placeholder: "Rechercher et sélectionner",
      selectedOptions: "Options sélectionnées",
      noSelection: "Aucune option sélectionnée",
      removeOption: "Retirer {{label}}",
    },

    file: {
      browse: "Parcourir les fichiers",
      noneSelected: "Aucun fichier sélectionné",
      selected_one: "{{count}} fichier sélectionné",
      selected_other: "{{count}} fichiers sélectionnés",
      remove: "Retirer {{fileName}}",
    },

    dropZone: {
      defaultLabel: "Téléversement de fichiers",
      instructions:
        "Glissez-déposez des fichiers ici, collez-les ou sélectionnez-les sur votre appareil.",
    },

    datePicker: {
      openCalendar: "Ouvrir le calendrier",
      previousMonth: "Mois précédent",
      nextMonth: "Mois suivant",
    },

    loading: {
      default: "Chargement",
      content: "Chargement du contenu",
      table: "Chargement du tableau",
      card: "Chargement de la carte",
      processing: "Traitement…",
      retrying: "Nouvelle tentative…",
    },

    emptyState: {
      defaultTitle: "Aucune information disponible",
    },

    errorState: {
      alertTitle: "Erreur",
      defaultTitle: "Une erreur est survenue",
      defaultDescription:
        "Les renseignements demandés n’ont pas pu être chargés.",
    },

    notifications: {
      dismiss: "Fermer",
    },

    userProfileMenu: {
      open: "Ouvrir le menu du profil utilisateur",

      menuLabel: "Options du profil utilisateur",

      profile: "Mon profil",

      profileDescription: "Afficher et modifier votre profil",

      settings: "Paramètres",

      settingsDescription: "Gérer les préférences de votre compte",

      signOut: "Se déconnecter",

      signingOut: "Déconnexion…",

      signOutDescription: "Mettre fin à votre session actuelle",
    },

    dataTable: {
      searchLabel: "Rechercher dans le tableau",
      searchPlaceholder: "Rechercher…",
      actions: "Actions",
      export: "Exporter",
      exporting: "Exportation…",
      exportFailed:
        "Les données n'ont pas pu être exportées. Veuillez réessayer.",
      selectAll: "Sélectionner toutes les lignes",
      emptyTitle: "Aucun enregistrement trouvé",

      pagination: {
        previous: "Précédente",
        next: "Suivante",
        page: "Page {{current}} sur {{total}}",
        pageSize: "Lignes par page",
        summary: "Affichage de {{from}} à {{to}} sur {{total}} enregistrements",
      },
    },
  },

  componentShowcase: {
    page: {
      eyebrow: "Système de conception",
      title: "Présentation des composants",
      description:
        "Exemples des composants d’interface partagés disponibles pour les fonctionnalités TPM.",
      openDialog: "Ouvrir la boîte de dialogue",
      openAlert: "Ouvrir l’alerte",
    },

    textFields: {
      title: "Champs de texte",
      description:
        "Exemples de texte, de courriel, de mot de passe et de validation.",

      email: {
        label: "Adresse courriel",
        placeholder: "utilisateur@exemple.com",
        description: "Utilisée pour les notifications du compte.",
      },

      password: {
        label: "Mot de passe",
        placeholder: "Saisissez votre mot de passe",
        description: "Utilisez le mot de passe de votre compte TPM.",
      },

      accountCode: {
        label: "Code du compte",
        placeholder: "ACC-10001",
        error: "Le code du compte n’existe pas.",
      },

      readOnly: {
        label: "Valeur en lecture seule",
        value: "Valeur générée par le système",
      },

      disabled: {
        label: "Champ désactivé",
        value: "Non disponible",
      },
    },

    selection: {
      title: "Contrôles de sélection",
      description:
        "Composants de case à cocher et de sélection avec état contrôlé.",

      orderStatus: {
        label: "Statut de la commande",
        placeholder: "Sélectionner un statut de commande",
        description: "Filtrer les commandes selon leur statut actuel.",
      },

      selectedValue: "Valeur sélectionnée : {{value}}",

      rememberPreference: {
        label: "Mémoriser ma préférence",
        description: "Enregistrer cette préférence de filtre sur cet appareil.",
      },

      includeInactive: {
        label: "Inclure les commandes inactives",
        description: "Afficher les commandes annulées et supprimées.",
      },

      requiredAgreement: {
        label: "Accord obligatoire",
        error: "Vous devez accepter cette option.",
      },

      disabledOption: "Option désactivée",

      statuses: {
        draft: {
          label: "Brouillon",
          description: "La commande n’a pas été soumise.",
        },

        submitted: {
          label: "Soumise",
          description: "La commande est en attente de traitement.",
        },

        pendingApproval: {
          label: "En attente d’approbation",
          description: "La commande nécessite l’approbation d’un gestionnaire.",
        },

        approved: {
          label: "Approuvée",
          description: "La commande a terminé le processus d’approbation.",
        },

        cancelled: {
          label: "Annulée",
          description: "La commande n’est plus active.",
        },
      },
    },

    buttons: {
      title: "Boutons et chargement",
      description: "Variantes de boutons et états de chargement.",
      primary: "Principal",
      secondary: "Secondaire",
      ghost: "Transparent",
      danger: "Danger",
      disabled: "Désactivé",
      loadingOrders: "Chargement des commandes",
      processingOrder: "Traitement de la commande",
      viewDialog: "Afficher la boîte de dialogue",
    },

    cards: {
      title: "Variantes de cartes",
      description:
        "Les cartes peuvent combiner des en-têtes, du contenu et des pieds de page.",

      configurationValid: {
        title: "Configuration valide",
        description: "La configuration du client a été chargée avec succès.",
      },

      transparent: {
        title: "Carte transparente",
        description: "Utile pour regrouper du contenu sans surface visible.",
      },
    },

    advancedInputs: {
      title: "Entrées avancées",
      description:
        "Groupes de saisie, dates, commutateurs et sélections radio.",

      orderAmount: "Montant de la commande",
      orderDate: "Date de la commande",

      paymentMethod: {
        label: "Mode de paiement",

        invoice: {
          label: "Facture",
          description: "Payer selon le processus de facturation habituel.",
        },

        creditCard: {
          label: "Carte de crédit",
          description: "Débiter la carte d’entreprise enregistrée.",
        },
      },

      notifications: {
        label: "Envoyer des notifications",
        description:
          "Envoyer un courriel au compte lorsque la commande change.",
      },

      permissions: {
        label: "Autorisations",
        description: "Sélectionnez une ou plusieurs autorisations.",
        readOrders: "Consulter les commandes",
        createOrders: "Créer des commandes",
        submitOrders: "Soumettre des commandes",
        approveAsRsm: "Approuver comme RSM",
      },
    },

    fileUploads: {
      title: "Téléversement de fichiers",
      description:
        "Sélection de fichiers standard et pièces jointes par glisser-déposer.",

      supportingDocument: {
        label: "Document justificatif",
        description: "Fichiers PDF ou images.",
      },

      attachments: {
        label: "Pièces jointes de la commande",
        description: "Maximum de 5 fichiers, 10 Mo chacun.",
      },
    },

    feedback: {
      title: "États de rétroaction",
      description:
        "États réutilisables pour les requêtes, les tableaux et les pages de fonctionnalités.",

      empty: {
        title: "Aucune commande trouvée",
        description:
          "Aucune commande ne correspond aux filtres sélectionnés. Modifiez les filtres ou créez une nouvelle commande.",
      },

      error: {
        title: "Les commandes n’ont pas pu être chargées",
        description:
          "Le serveur n’a pas retourné les commandes demandées. Vérifiez votre connexion et réessayez.",
      },
    },

    skeletons: {
      title: "Squelettes de chargement",
      description:
        "Espaces réservés qui préservent la mise en page pendant le chargement des données.",

      basic: {
        title: "Squelettes de base",
        description:
          "Espaces réservés individuels pour le texte, les avatars, les images et les contrôles.",
      },

      loadingOrderSummary: "Chargement du résumé de la commande",

      loadingAccountDetails: "Chargement des détails du compte",

      loadingOrders: "Chargement des commandes",
    },

    dialog: {
      title: "Créer une commande de test",
      description:
        "Cette boîte de dialogue présente les composants de formulaire réutilisables dans une fenêtre modale.",
      saveOrder: "Enregistrer la commande",

      orderReference: {
        label: "Référence de la commande",
        placeholder: "Saisissez une référence",
      },

      initialStatus: "Statut initial",

      sendNotification: "Envoyer une notification après l’enregistrement",
    },

    alertDialog: {
      title: "Supprimer le brouillon de commande?",
      description:
        "Ce brouillon et ses articles seront supprimés définitivement. Cette opération ne peut pas être annulée.",
      confirm: "Supprimer la commande",
      cancel: "Conserver la commande",
    },

    navigationComponents: {
      title: "Badges, fils d’Ariane et onglets",
      description:
        "Indicateurs d’état et modèles de navigation créés avec les composants d’interface partagés.",
    },

    badges: {
      title: "Badges",
      description:
        "Variantes de badges pour les statuts, les catégories et les métadonnées concises.",
      variants: "Variantes",
      withStatusDot: "Indicateurs d’état",
      sizes: "Tailles",
      withIcons: "Avec icônes",
      neutral: "Neutre",
      pending: "En attente d’approbation",
      awaitingReview: "En attente de révision",
      small: "Petit",
      medium: "Moyen",
      complete: "Terminé",
      inProgress: "En cours",
    },

    breadcrumbs: {
      title: "Fils d’Ariane",
      description:
        "Afficher la page actuelle dans la hiérarchie de l’application.",
      simple: "Chemin simple",
      nested: "Chemin imbriqué",
      longPath: "Chemin plus long",
      orderReference: "Commande ORD-10042",
    },

    tabs: {
      underline: {
        title: "Onglets soulignés",
        description: "Un style léger pour les sections principales d’une page.",
      },

      pills: {
        title: "Onglets en pilules",
        description:
          "Un style contenu adapté aux filtres et aux vues compactes.",
      },

      vertical: {
        title: "Onglets verticaux",
        description:
          "Une disposition verticale pour les pages de configuration et de paramètres.",
      },

      orderInformation: "Renseignements sur la commande",
      orderFilters: "Filtres de commande",
      configuration: "Sections de configuration",

      details: "Détails de la commande",
      attachments: "Pièces jointes",
      history: "Historique",
      settings: "Paramètres",

      open: "Ouvertes",
      general: "Général",
      users: "Utilisateurs",
      approval: "Approbation",

      content: {
        detailsTitle: "Détails de la commande",
        detailsDescription:
          "Consultez le compte, le distributeur, le type de commande et les renseignements financiers.",

        attachmentsTitle: "Pièces jointes de la commande",
        attachmentsDescription:
          "Consultez les fichiers et les documents justificatifs associés à cette commande.",

        historyTitle: "Historique de la commande",
        historyDescription:
          "Consultez les changements de statut et les autres activités enregistrées.",

        settingsDescription:
          "Les paramètres ne sont pas disponibles dans cet exemple.",

        openOrders:
          "Cette vue contient les commandes ouvertes ou en attente d’une action.",

        approvedOrders:
          "Cette vue contient les commandes ayant terminé le processus d’approbation.",

        cancelledOrders:
          "Cette vue contient les commandes qui ne sont plus actives.",

        generalTitle: "Configuration générale",
        generalDescription:
          "Gérez la configuration de base du client et de la gestion des commandes.",

        usersTitle: "Accès des utilisateurs",
        usersDescription:
          "Gérez les utilisateurs, les rôles et l’accès aux fonctionnalités de la plateforme.",

        approvalTitle: "Processus d’approbation",
        approvalDescription:
          "Configurez les approbations automatiques, à une étape et à deux étapes.",
      },
    },

    progressBars: {
      title: "Barres de progression",
      description:
        "Indicateurs de progression accessibles pour le chargement, le téléversement et les opérations à plusieurs étapes.",

      determinate: {
        title: "Progression déterminée",
        description: "Utilisez-la lorsque la progression actuelle est connue.",
      },

      interactive: {
        title: "Progression interactive",
        description:
          "Une barre de progression contrôlée par l’état de l’application.",
      },

      sizes: {
        title: "Tailles des barres de progression",
        description: "Choisissez une taille selon l’importance de l’opération.",
      },

      indeterminate: {
        title: "Progression indéterminée",
        description:
          "Utilisez-la lorsqu’une opération est active, mais que son pourcentage d’achèvement est inconnu.",
      },

      orderCompletion: "Progression de la commande",

      fileUpload: "Téléversement des pièces jointes",

      fileUploadDescription:
        "Téléversement des documents de la commande vers le serveur.",

      approvalSteps: "Processus d’approbation",

      stepsCompleted: "{{current}} étapes sur {{total}}",

      failedImport: "Importation terminée avec des erreurs",

      processingOrders: "Traitement des commandes",

      decrease: "Réduire la progression",

      increase: "Augmenter la progression",

      small: "Petite barre de progression",

      medium: "Barre de progression moyenne",

      large: "Grande barre de progression",

      loadingConfiguration: "Chargement de la configuration du client",

      submittingOrder: "Soumission de la commande",

      submittingDescription:
        "Veuillez garder cette page ouverte pendant la soumission de la commande.",

      backgroundProcessing: "Traitement en arrière-plan",
    },

    notifications: {
      title: "Notifications",

      description:
        "Messages temporaires pour les réussites, avertissements, erreurs et traitements en arrière-plan.",

      basic: {
        title: "Types de notifications",

        description:
          "Affichez les variantes de notification standard utilisées dans l’application.",

        guidance:
          "Les notifications communiquent des mises à jour temporaires sans déplacer le focus du clavier.",
      },

      async: {
        title: "Opérations asynchrones",

        description:
          "Mettez à jour une notification de chargement lorsqu’une opération réussit ou échoue.",

        explanation:
          "La notification de chargement est mise à jour sur place afin que l’utilisateur perçoive une seule opération continue.",
      },

      management: {
        title: "Gestion des notifications",

        description:
          "Affichez une progression persistante ou fermez les notifications actives.",

        guidance:
          "Les notifications de chargement restent visibles jusqu’à ce que l’application les mette à jour ou les ferme.",
      },

      actions: {
        showSuccess: "Afficher une réussite",

        showInfo: "Afficher une information",

        showWarning: "Afficher un avertissement",

        showError: "Afficher une erreur",

        simulateSuccess: "Simuler une soumission réussie",

        simulateFailure: "Simuler un échec de soumission",

        showLoading: "Afficher une notification de chargement",

        dismissAll: "Fermer toutes les notifications",
      },

      messages: {
        success: "La commande a été enregistrée avec succès.",

        info: "Le brouillon de la commande a été mis à jour avec les renseignements les plus récents.",

        warning:
          "La commande nécessite des renseignements supplémentaires avant la soumission.",

        error: "La commande n’a pas pu être enregistrée.",

        submitting: "Soumission de la commande…",

        submitted: "La commande a été soumise avec succès.",

        submitFailed: "La commande n’a pas pu être soumise.",

        backgroundProcessing: "Traitement des commandes en arrière-plan…",
      },
    },

    dataTables: {
      title: "Tableaux de données adaptatifs",

      description:
        "Tableaux triables avec colonnes fixes pour les actions et les identifiants sur petits écrans.",

      responsiveHint:
        "Défilez horizontalement pour afficher les autres colonnes. Les actions et identifiants restent visibles.",

      actions: {
        view: "Afficher",
        select: "Sélectionner",
        review: "Réviser",
      },

      columns: {
        orderNumber: "Numéro de commande",

        account: "Compte",

        accountCode: "Code du compte",

        accountName: "Nom du compte",

        orderType: "Type de commande",

        status: "Statut",

        netAmount: "Montant net",

        createdOn: "Date de création",

        city: "Ville",

        province: "Province",

        distributor: "Distributeur",

        submittedBy: "Soumise par",

        approvalStep: "Étape d’approbation",

        waitingTime: "Temps d’attente",
      },

      status: {
        draft: "Brouillon",
        pending: "En attente d’approbation",
        approved: "Approuvée",
        active: "Actif",
        inactive: "Inactif",
      },

      orders: {
        title: "Afficher et naviguer",

        description:
          "Tableau avec action d’affichage, colonnes triables et pagination serveur.",

        tableLabel: "Exemple de commandes",

        scrollLabel: "Tableau déroulant des exemples de commandes",

        viewOrder: "Afficher la commande {{orderNumber}}",

        opened: "Ouverture de la commande {{orderNumber}}.",

        exported: "Les commandes ont été exportées avec succès.",
        pagination: "Affichage de {{from}} à {{to}} sur {{total}} commandes",
      },

      accounts: {
        title: "Sélectionner un enregistrement",

        description:
          "Un tableau de sélection dans lequel la première colonne exécute une action explicite du processus.",

        tableLabel: "Exemple de comptes",

        scrollLabel: "Tableau déroulant des exemples de comptes",

        selectAccount: "Sélectionner le compte {{accountCode}}",

        selectedAccount: "Le compte {{accountCode}} est sélectionné",

        selectedNotification: "Le compte {{accountCode}} a été sélectionné.",

        selectedSummary: "Sélectionné : {{accountCode}} — {{accountName}}",

        noSelection: "Sélectionnez un compte dans le tableau pour continuer.",
      },

      approvals: {
        title: "Comportement sur écran étroit",

        description:
          "Un tableau volontairement restreint pour vérifier les colonnes fixes et le défilement horizontal.",

        constrainedDescription:
          "Cet exemple est limité à un conteneur étroit. Faites défiler horizontalement et vérifiez que Réviser et Numéro de commande restent visibles.",

        tableLabel: "Exemple de file d’approbation",

        scrollLabel: "File d’approbation déroulante",

        reviewOrder: "Réviser la commande {{orderNumber}}",

        opened: "Ouverture de l’approbation pour la commande {{orderNumber}}.",

        waitingDays_one: "{{count}} jour",

        waitingDays_other: "{{count}} jours",
      },

      selection: {
        title: "Sélection multiple",

        description:
          "Une colonne d’action à cases à cocher qui recueille les sélections pour les opérations groupées.",

        tableLabel: "Exemple de comptes sélectionnables",

        selectedCount_one: "{{count}} compte sélectionné",

        selectedCount_other: "{{count}} comptes sélectionnés",
      },

      server: {
        title: "Pagination côté serveur",

        description:
          "Le tri, la recherche et la pagination sont délégués au serveur par un seul rappel de requête consolidé.",

        tableLabel: "Exemple de commandes paginées côté serveur",

        note: "Le tableau transmet une requête; la fonctionnalité récupère la page et le nombre total d’enregistrements.",
      },

      pagination: {
        previous: "Précédente",
        next: "Suivante",
        rowsPerPage: "Lignes par page",

        page: "Page {{current}} sur {{total}}",

        showcaseOnly:
          "La pagination est affichée comme exemple dans la vitrine des composants.",
      },
    },
  },

  dashboard: {
    title: "Tableau de bord",
    description: "Vue d’ensemble de la gestion des commandes TPM.",

    cards: {
      openOrders: "Commandes ouvertes",
      pendingApproval: "En attente d’approbation",
      submittedToday: "Soumises aujourd’hui",
      activePromotions: "Promotions actives",
    },

    foundation: {
      title: "Fondation du projet",
      description:
        "L’interface, le routeur, le client de requêtes, le thème et la structure par fonctionnalité fonctionnent.",
    },
  },
} satisfies LocaleDefinition<EnglishTranslation>["translation"];

const frenchLocale = {
  metadata: {
    code: "fr",
    locale: "fr-CA",
    displayName: "Français",
    shortName: "FR",
  },

  translation,
} satisfies LocaleDefinition<EnglishTranslation>;

export default frenchLocale;
